import { forEach, map } from 'lodash-es'
import { FC, useCallback, useEffect, useState } from 'react'
import {
  Control,
  Controller,
  FieldArrayWithId,
  FieldErrors,
  UseFieldArrayRemove,
  UseFormRegister,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch,
  useFieldArray,
} from 'react-hook-form'

import {
  TCurrency,
  TFestivalOrderTypes,
  TFestivalPaymentProvider,
  TFestivalPaymentType,
  TFormValues,
} from './GroupsTab'
import Loader from '../../../common/components/Loader'
import {
  ApplePayConfig,
  GooglePayConfig,
  IGroupFestivalPayload,
  PaymentProvider,
} from '../../../common/models/groups'
import FormGroup from '../../../core/components/FormGroup'
import {
  createApplePayConfig,
  createGooglePayConfig,
  deleteApplePayConfig,
  deleteGooglePayConfig,
  deleteGroupFestival,
  updateApplePayConfig,
  updateGooglePayConfig,
  updateGroupFestival,
} from '../../../core/services/groups'
import { useAppSelector } from '../../../store'
import { DeleteConfirmationModal } from '../DeleteConfirmationModal'
import { MessageBox } from '../messages/MessageBox'
import { Button } from '@/components/ui/button'
import SelectField from '@/components/select-field'
import { Checkbox } from '@/components/ui/checkbox'
import { Card } from '@/components/ui'
import { Icon } from '@/common/components/Icon'
import { ApplePayFields } from './payment-providers/ApplePayFields'
import { GooglePayFields } from './payment-providers/GooglePayFields'
import {
  mapApplePayConfigToApi,
  mapGooglePayConfigToApi,
} from './payment-providers/enumMappers'
import {
  IApplePayConfigForm,
  IGooglePayConfigForm,
} from './payment-providers/types'
import {
  loadApplePayConfig,
  loadGooglePayConfig,
} from './payment-providers/configLoaders'

type OrderType = {
  value: TFestivalOrderTypes
  label: string
}

type PaymentType = {
  value: TFestivalPaymentType
  label: string
}

type Props = {
  festival: FieldArrayWithId<TFormValues, `groups.${number}.festivals`, 'id'>
  control: Control<TFormValues>
  errors: FieldErrors<TFormValues>
  register: UseFormRegister<TFormValues>
  index: number
  remove: UseFieldArrayRemove
  groupIndex: number
  watch: UseFormWatch<TFormValues>
  currenciesList?: TCurrency[]
  setValue: UseFormSetValue<TFormValues>
  trigger: UseFormTrigger<TFormValues>
  paymentProviders: PaymentProvider[]
}

const emptyApplePayConfig = (festivalId: string): IApplePayConfigForm => ({
  festivalId,
  countryCode: '',
  merchantIdentifier: '',
  displayName: '',
  supportedNetworks: [],
  merchantCapabilities: [],
  stripePublishableKey: '',
})

const emptyGooglePayConfig = (festivalId: string): IGooglePayConfigForm => ({
  festivalId,
  countryCode: '',
  environment: '',
  gateway: '',
  merchantName: '',
  merchantId: '',
  cardNetworks: [],
  authMethods: [],
  stripeVersion: '',
  stripePublishableKey: '',
})

export const GroupFestival: FC<Props> = ({
  errors,
  index,
  remove,
  groupIndex,
  watch,
  currenciesList,
  control,
  register,
  setValue,
  trigger,
  paymentProviders,
}): JSX.Element => {
  const [deleteConfirmation, setDeleteConfirmation] = useState<boolean>(false)
  const [
    deleteConfirmationPaymentProvider,
    setDeleteConfirmationPaymentProvider,
  ] = useState<boolean>(false)
  const [saving, setSaving] = useState<boolean>(false)
  const [deleteIndex, setDeleteIndex] = useState<number | undefined>()
  const [deletePaymentProviderIndex, setDeletePaymentProviderIndex] = useState<
    number | undefined
  >()
  const { festivals } = useAppSelector((s) => s.app)

  const orderTypes: OrderType[] = [
    { value: 'TICKETS', label: 'Tickets' },
    { value: 'CHECKIN', label: 'Check-in' },
    { value: 'TRANSFER', label: 'Transfer' },
    { value: 'TOPUP', label: 'Top-up' },
  ]

  const paymentTypes: PaymentType[] = [
    { value: 'CARD', label: 'Card' },
    { value: 'CRYPTO', label: 'Crypto' },
    { value: 'GOOGLE_PAY', label: 'Google Pay' },
    { value: 'APPLE_PAY', label: 'Apple Pay' },
  ]

  const [isError, setIsError] = useState<boolean>(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')

  useEffect(() => {
    const festivals = watch(`groups.${groupIndex}.festivals`)
    forEach(festivals, (festival, i) => {
      const currencies = festival.currencies
      forEach(currencies, (currency, j) => {
        setValue(
          `groups.${groupIndex}.festivals.${i}.currencies.${j}.name`,
          currency.name,
        )
        setValue(
          `groups.${groupIndex}.festivals.${i}.currencies.${j}.id`,
          currency.id,
        )
      })
    })
  }, [currenciesList])

  // Create/update Apple & Google Pay configs. Runs BEFORE the festival update so
  // the festival's payment-provider DTOs can reference an existing config.
  const upsertPayConfigs = async (
    festivalId: string,
    dtos: TFestivalPaymentProvider[],
  ) => {
    await Promise.all(
      map(dtos, async (dto, i) => {
        // Apple Pay
        if (dto.paymentType === 'APPLE_PAY' && dto.applePayConfig) {
          const cfg = dto.applePayConfig
          const payload: Omit<ApplePayConfig, 'id'> = {
            festivalId,
            paymentProviderId: dto.paymentProviderId,
            merchantIdentifier: cfg.merchantIdentifier,
            displayName: cfg.displayName,
            countryCode: cfg.countryCode,
            ...mapApplePayConfigToApi(cfg),
            stripePublishableKey: cfg.stripePublishableKey,
          }
          const { data } = cfg.configId
            ? await updateApplePayConfig(
                cfg.configId,
                payload,
                cfg.certificateFile,
                cfg.privateKeyFile,
                cfg.clearKeys,
              )
            : await createApplePayConfig(
                payload,
                cfg.certificateFile,
                cfg.privateKeyFile,
              )
          // Reflect stored-key flags from the response and clear the staged
          // upload so a follow-up save doesn't re-send the files.
          setValue(
            `groups.${groupIndex}.festivals.${index}.festivalPaymentProviderDtos.${i}.applePayConfig`,
            {
              ...cfg,
              configId: data?.id ?? cfg.configId,
              hasCertificate: data?.hasCertificate,
              hasPrivateKey: data?.hasPrivateKey,
              certificateFile: null,
              privateKeyFile: null,
              clearKeys: false,
            },
          )
        }

        // Google Pay
        if (dto.paymentType === 'GOOGLE_PAY' && dto.googlePayConfig) {
          const cfg = dto.googlePayConfig
          const payload: Omit<GooglePayConfig, 'id'> = {
            festivalId,
            paymentProviderId: dto.paymentProviderId,
            countryCode: cfg.countryCode,
            environment: cfg.environment,
            gateway: cfg.gateway,
            merchantName: cfg.merchantName,
            merchantId: cfg.merchantId,
            ...mapGooglePayConfigToApi(cfg),
            stripeVersion: cfg.stripeVersion,
            stripePublishableKey: cfg.stripePublishableKey,
          }
          if (cfg.configId) {
            await updateGooglePayConfig(cfg.configId, payload)
          } else {
            const { data } = await createGooglePayConfig(payload)
            setValue(
              `groups.${groupIndex}.festivals.${index}.festivalPaymentProviderDtos.${i}.googlePayConfig.configId`,
              data?.id,
            )
          }
        }
      }),
    )
  }

  // Delete configs whose payment type was switched away. Runs AFTER the festival
  // update so the festival no longer references the deleted config.
  const deleteOrphanedPayConfigs = async (dtos: TFestivalPaymentProvider[]) => {
    await Promise.all(
      map(dtos, async (dto) => {
        if (dto.paymentType !== 'APPLE_PAY' && dto.applePayConfig?.configId) {
          await deleteApplePayConfig(dto.applePayConfig.configId)
        }
        if (dto.paymentType !== 'GOOGLE_PAY' && dto.googlePayConfig?.configId) {
          await deleteGooglePayConfig(dto.googlePayConfig.configId)
        }
      }),
    )
  }

  const updateFestival = useCallback(async () => {
    const valid = await trigger(`groups.${groupIndex}.festivals.${index}`)
    if (!valid) return
    setSaving(true)
    try {
      const groupId = watch(`groups.${groupIndex}.id`)
      const festival = watch(`groups.${groupIndex}.festivals.${index}`)

      if (!groupId || !festival) return

      const festivalPayload: IGroupFestivalPayload = {
        festivalId: festival.id,
        currencies: map(festival.currencies, (currency) => ({
          id: currency?.id || '',
          active: currency.active as boolean,
          default: currency.main as boolean,
          name: currency.name,
        })),
        festivalPaymentProviderDtos: map(
          festival.festivalPaymentProviderDtos,
          (provider) => ({
            orderTypes: [
              ...new Set(provider.orderTypes?.filter(Boolean) ?? []),
            ],
            paymentProviderId: provider.paymentProviderId,
            paymentType: provider.paymentType,
          }),
        ),
      }

      await upsertPayConfigs(
        festival.id,
        festival.festivalPaymentProviderDtos ?? [],
      )
      await updateGroupFestival(groupId, festival.id, festivalPayload)
      await deleteOrphanedPayConfigs(festival.festivalPaymentProviderDtos ?? [])
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    } finally {
      setSaving(false)
    }
  }, [])

  const deleteFestival = useCallback(async () => {
    try {
      setSaving(true)
      const groupId = watch(`groups.${groupIndex}.id`)
      const festivalId = watch(`groups.${groupIndex}.festivals.${index}.id`)
      if (!groupId || !festivalId) return
      await deleteGroupFestival(groupId, festivalId)
      remove(index as number)
      setDeleteConfirmation(false)
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    } finally {
      setSaving(false)
    }
  }, [])

  const {
    fields: festivalPaymentProviderDtosFields,
    append: appendFestivalPaymentProviderDtos,
    remove: removeFestivalPaymentProviderDtos,
  } = useFieldArray({
    control: control,
    name: `groups.${groupIndex}.festivals.${index}.festivalPaymentProviderDtos`,
  })

  const festivalPaymentProviderDtos = watch(
    `groups.${groupIndex}.festivals.${index}.festivalPaymentProviderDtos`,
  )

  return (
    <>
      {saving && <Loader />}
      <Card className="py-4 px-4 mt-2 !bg-monochrome-3-60 space-y-2">
        <Controller
          control={control}
          name={`groups.${groupIndex}.festivals.${index}.id`}
          render={({ field }) => (
            <FormGroup label="Select Festival">
              <div className="flex w-full items-center justify-between gap-4">
                <SelectField
                  outline
                  error={
                    errors?.groups?.[groupIndex]?.festivals?.[index]?.id
                      ?.message
                  }
                  triggerClassName="h-[44px] rounded-2xl"
                  wrapperClassName="w-full"
                  placeholder="Select Festival"
                  options={festivals
                    .filter((festival) => {
                      const groupFestivals = watch(
                        `groups.${groupIndex}.festivals`,
                      )
                      const usedFestivalIds = groupFestivals
                        ?.map((f, idx) => (idx === index ? null : f?.id))
                        .filter(Boolean)
                      if (festival.id === field.value) return true
                      return !usedFestivalIds?.includes(festival.id)
                    })
                    .map((festival) => ({
                      value: festival.id,
                      label: festival.name,
                    }))}
                  value={field.value}
                  onValueChange={field.onChange}
                />
                <Button
                  variant="destructive"
                  className="w-[154px]"
                  type="button"
                  onClick={() => {
                    setDeleteConfirmation(true)
                    setDeleteIndex(index)
                  }}
                >
                  Delete
                  <Icon
                    icon="close-circle"
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                  />
                </Button>
              </div>
            </FormGroup>
          )}
        />
        {map(festivalPaymentProviderDtosFields, (field, i) => {
          const festivalPaymentProviderDtos = watch(
            `groups.${groupIndex}.festivals.${index}.festivalPaymentProviderDtos`,
          )
          const currentPaymentType = watch(
            `groups.${groupIndex}.festivals.${index}.festivalPaymentProviderDtos.${i}.paymentType`,
          )
          const currentOrderTypes = watch(
            `groups.${groupIndex}.festivals.${index}.festivalPaymentProviderDtos.${i}.orderTypes`,
          )
          const festivalId =
            watch(`groups.${groupIndex}.festivals.${index}.id`) ?? ''
          return (
            <Card
              key={field.id}
              className="bg-monochrome-3-60 border border-monochrome-120 p-4 mt-2"
            >
              <Controller
                name={`groups.${groupIndex}.festivals.${index}.festivalPaymentProviderDtos.${i}.paymentProviderId`}
                control={control}
                render={({ field: paymentProviderIdField }) => (
                  <FormGroup label="Select Payment Provider">
                    <SelectField
                      error={
                        errors?.groups?.[groupIndex]?.festivals?.[index]
                          ?.festivalPaymentProviderDtos?.[i]?.paymentProviderId
                          ?.message
                      }
                      outline
                      triggerClassName="h-[44px] rounded-2xl"
                      wrapperClassName="w-full"
                      placeholder="Select Payment Provider"
                      options={map(paymentProviders, (provider) => ({
                        value: provider.id,
                        label: provider.title,
                      }))}
                      value={paymentProviderIdField.value}
                      onValueChange={paymentProviderIdField.onChange}
                    />
                  </FormGroup>
                )}
              />
              <div className="grid grid-cols-4 divide-x divide-monochrome-120 gap-4 border-b border-monochrome-120 pb-4 mb-4 mt-4">
                {map(orderTypes, (orderType, ind) => {
                  const currentValue = watch(
                    `groups.${groupIndex}.festivals.${index}.festivalPaymentProviderDtos.${i}.orderTypes`,
                  )
                  return (
                    <Controller
                      key={`active-${ind}`}
                      name={`groups.${groupIndex}.festivals.${index}.festivalPaymentProviderDtos.${i}.orderTypes`}
                      control={control}
                      render={({ field: orderTypeField }) => (
                        <FormGroup
                          label={orderType.label}
                          labelClassName="whitespace-nowrap"
                          inputWrapperClassName="w-auto"
                          wrapperClassName="flex-row-reverse justify-end w-auto pe-4"
                          direction="row"
                          labelProps={{
                            htmlFor: `order-${ind}`,
                          }}
                        >
                          <Checkbox
                            id={`order-${ind}`}
                            disabled={
                              !orderTypeField.value?.includes(
                                orderType.value as TFestivalOrderTypes,
                              ) &&
                              !!currentPaymentType &&
                              festivalPaymentProviderDtos?.some(
                                (o, otherIdx) =>
                                  otherIdx !== i &&
                                  o.paymentType === currentPaymentType &&
                                  o.orderTypes?.includes(orderType.value),
                              )
                            }
                            onCheckedChange={() => {
                              const safeValue = currentValue ?? []

                              const newValue = safeValue.includes(
                                orderType.value,
                              )
                                ? safeValue.filter(
                                    (one) => one !== orderType.value,
                                  )
                                : [...safeValue, orderType.value]

                              setValue(
                                `groups.${groupIndex}.festivals.${index}.festivalPaymentProviderDtos.${i}.orderTypes`,
                                newValue,
                              )
                            }}
                            checked={currentValue?.includes(
                              orderType.value as TFestivalOrderTypes,
                            )}
                          />
                        </FormGroup>
                      )}
                    />
                  )
                })}
              </div>
              <Controller
                name={`groups.${groupIndex}.festivals.${index}.festivalPaymentProviderDtos.${i}.paymentType`}
                control={control}
                render={({ field: paymentTypeField }) => (
                  <FormGroup label="Payment Type">
                    <SelectField
                      error={
                        errors?.groups?.[groupIndex]?.festivals?.[index]
                          ?.festivalPaymentProviderDtos?.[i]?.paymentType
                          ?.message
                      }
                      outline
                      triggerClassName="h-[44px] rounded-2xl"
                      wrapperClassName="w-full"
                      placeholder="Select Payment Type"
                      options={map(paymentTypes, (paymentType) => ({
                        value: paymentType.value,
                        label: paymentType.label,
                        disabled:
                          paymentType.value !== paymentTypeField.value &&
                          festivalPaymentProviderDtos?.some(
                            (o, otherIdx) =>
                              otherIdx !== i &&
                              o.paymentType === paymentType.value &&
                              o.orderTypes?.some((ot) =>
                                currentOrderTypes?.includes(ot),
                              ),
                          ),
                      }))}
                      value={paymentTypeField.value}
                      onValueChange={async (value) => {
                        paymentTypeField.onChange(value)
                        if (value !== 'APPLE_PAY' && value !== 'GOOGLE_PAY') {
                          return
                        }
                        const paymentProviderId = watch(
                          `groups.${groupIndex}.festivals.${index}.festivalPaymentProviderDtos.${i}.paymentProviderId`,
                        )
                        const festivalName = festivals.find(
                          (f) => f.id === festivalId,
                        )?.platformName
                        try {
                          setSaving(true)
                          if (value === 'APPLE_PAY') {
                            const existing = paymentProviderId
                              ? await loadApplePayConfig(
                                  paymentProviderId,
                                  festivalName,
                                )
                              : undefined
                            setValue(
                              `groups.${groupIndex}.festivals.${index}.festivalPaymentProviderDtos.${i}.applePayConfig`,
                              existing ?? emptyApplePayConfig(festivalId),
                            )
                          } else {
                            const existing = paymentProviderId
                              ? await loadGooglePayConfig(
                                  paymentProviderId,
                                  festivalName,
                                )
                              : undefined
                            setValue(
                              `groups.${groupIndex}.festivals.${index}.festivalPaymentProviderDtos.${i}.googlePayConfig`,
                              existing ?? emptyGooglePayConfig(festivalId),
                            )
                          }
                        } finally {
                          setSaving(false)
                        }
                      }}
                    />
                  </FormGroup>
                )}
              />
              {currentPaymentType === 'APPLE_PAY' && (
                <div className="mt-4">
                  <ApplePayFields
                    namePrefix={`groups.${groupIndex}.festivals.${index}.festivalPaymentProviderDtos.${i}.applePayConfig`}
                    control={control}
                    register={register}
                    setValue={setValue}
                    configErrors={
                      errors?.groups?.[groupIndex]?.festivals?.[index]
                        ?.festivalPaymentProviderDtos?.[i]?.applePayConfig
                    }
                  />
                </div>
              )}
              {currentPaymentType === 'GOOGLE_PAY' && (
                <div className="mt-4">
                  <GooglePayFields
                    namePrefix={`groups.${groupIndex}.festivals.${index}.festivalPaymentProviderDtos.${i}.googlePayConfig`}
                    control={control}
                    register={register}
                    setValue={setValue}
                    configErrors={
                      errors?.groups?.[groupIndex]?.festivals?.[index]
                        ?.festivalPaymentProviderDtos?.[i]?.googlePayConfig
                    }
                  />
                </div>
              )}
              <div className="flex justify-end mt-4 [&>*]:w-[154px]">
                <Button
                  variant="destructive"
                  type="button"
                  className="w-[154px]"
                  onClick={() => {
                    setDeleteConfirmationPaymentProvider(true)
                    setDeletePaymentProviderIndex(i)
                  }}
                >
                  Delete
                  <Icon
                    icon="close-circle"
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                  />
                </Button>
              </div>
            </Card>
          )
        })}
        {paymentProviders.length > 0 && (
          <Button
            type="button"
            onClick={() => {
              appendFestivalPaymentProviderDtos({
                paymentProviderId: '',
                orderTypes: [],
              })
            }}
            variant="secondary"
            className="w-full mt-2 mb-2"
          >
            Add New Payment Provider
          </Button>
        )}
        {currenciesList && currenciesList?.length > 0 && (
          <div className="card-gray-with-border mt-2">
            <h4 className="mb-2 text-title-xs text-monochrome-1">
              Select Active Currencies
            </h4>
            <div className="grid grid-cols-4 divide-x divide-monochrome-120 gap-4 border-b border-monochrome-120 pb-4 mb-4 mt-4">
              {map(currenciesList, (currency, ind) => (
                <Controller
                  key={`active-${ind}`}
                  name={`groups.${groupIndex}.festivals.${index}.currencies.${ind}.active`}
                  control={control}
                  render={({ field }) => (
                    <FormGroup
                      label={currency.name}
                      labelClassName="whitespace-nowrap"
                      inputWrapperClassName="w-auto"
                      wrapperClassName="flex-row-reverse justify-end w-auto pe-4"
                      direction="row"
                      labelProps={{
                        htmlFor: `active-${ind}`,
                      }}
                    >
                      <Checkbox
                        id={`active-${ind}`}
                        onCheckedChange={field.onChange}
                        checked={field.value || false}
                      />
                    </FormGroup>
                  )}
                />
              ))}
            </div>
            <h4 className="mb-2 text-title-xs text-monochrome-1">
              Select Main Currency
            </h4>
            <div className="grid grid-cols-4 divide-x divide-monochrome-120 gap-4 border-b border-monochrome-120 pb-4 mb-4 mt-4">
              {map(currenciesList, (currency, ind) => (
                <Controller
                  key={`main-${ind}`}
                  name={`groups.${groupIndex}.festivals.${index}.currencies.${ind}.main`}
                  control={control}
                  render={({ field }) => (
                    <FormGroup
                      label={currency.name}
                      labelClassName="whitespace-nowrap"
                      inputWrapperClassName="w-auto"
                      wrapperClassName="flex-row-reverse justify-end w-auto pe-4"
                      direction="row"
                      labelProps={{
                        htmlFor: `main-${ind}`,
                      }}
                    >
                      <Checkbox
                        id={`main-${ind}`}
                        onCheckedChange={(checked) => {
                          const festivalCurrencies = watch(
                            `groups.${groupIndex}.festivals.${index}.currencies`,
                          )
                          festivalCurrencies?.forEach((_, i) => {
                            setValue(
                              `groups.${groupIndex}.festivals.${index}.currencies.${i}.main`,
                              i === ind ? checked === true : false,
                            )
                          })
                        }}
                        checked={field.value || false}
                      />
                    </FormGroup>
                  )}
                />
              ))}
            </div>
          </div>
        )}
        <div className="flex gap-2 justify-end mt-2 [&>*]:w-[154px]">
          <Button
            variant="secondary"
            className="flex items-center justify-center"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            type="button"
            className="flex items-center justify-center"
            onClick={updateFestival}
          >
            Save
          </Button>
        </div>
      </Card>
      <DeleteConfirmationModal
        showDeleteConfirmationModal={deleteConfirmation}
        setDeleteConfirmationModal={setDeleteConfirmation}
        deleteTitle={'Festival'}
        deleteFunc={() => {
          const saved = watch(`groups.${groupIndex}.festivals.${index}.saved`)
          if (saved) {
            deleteFestival()
          } else {
            remove(deleteIndex as number)
            setDeleteConfirmation(false)
          }
        }}
      />
      <DeleteConfirmationModal
        size="sm"
        centered
        showDeleteConfirmationModal={deleteConfirmationPaymentProvider}
        setDeleteConfirmationModal={setDeleteConfirmationPaymentProvider}
        deleteTitle={'Payment Provider'}
        deleteFunc={() => {
          removeFestivalPaymentProviderDtos(deletePaymentProviderIndex)
          setDeleteConfirmation(false)
        }}
      />
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
